import { Icon, Text } from 'components';
import { useOutsideClick } from 'core';
import { theme } from 'lib';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const Box = styled.div`
  position: relative;
  width: ${(props) => props.wid};
`;

// The shared Dropdown leaves 34px of its own width empty beside the pill, which read as a large
// gap between filters. This control is exactly as wide as it is drawn.
const Trigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  height: 41px;
  padding: 0 16px;
  border: 1px solid ${theme.GREY_50};
  border-radius: 48px;
  background: #fff;
  font: inherit;
  cursor: pointer;
  text-align: left;

  &:focus-visible {
    outline: 2px solid ${theme.DARK_BLUE};
    outline-offset: 2px;
  }
`;

const Label = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 100%;
  z-index: 20;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 0 16px rgba(0, 0, 0, 0.2);
  overflow: hidden;
`;

const Search = styled.input`
  width: 100%;
  box-sizing: border-box;
  height: 40px;
  padding: 0 16px;
  border: 0;
  border-bottom: 1px solid ${theme.GREY_20};
  font: inherit;
  outline: none;
`;

const List = styled.ul`
  margin: 0;
  padding: 4px 0;
  list-style: none;
  max-height: 280px;
  overflow-y: auto;
`;

const Item = styled.li`
  padding: 8px 16px;
  cursor: pointer;
  white-space: nowrap;
  background: ${(props) => (props.selected ? theme.GREY_10 : 'transparent')};

  &:hover {
    background: ${theme.GREY_10};
  }
`;

const Empty = styled.li`
  padding: 8px 16px;
  color: ${theme.GREY_60};
`;

// A pill that opens a list. With `searchable` the list filters as you type, for long lists such as
// countries, where scrolling a menu to find one entry is the slow way.
const FilterSelect = ({ label, wid, options, selected, onSelect, searchable }) => {
  const [open, setOpen] = useState(false),
    [query, setQuery] = useState(''),
    boxRef = useRef(null),
    searchRef = useRef(null);

  useOutsideClick(boxRef, () => open && close());

  useEffect(() => {
    if (open && searchable) searchRef.current?.focus();
  }, [open, searchable]);

  function close() {
    setOpen(false);
    setQuery('');
  }

  function choose(option) {
    onSelect(option);
    close();
  }

  const needle = query.trim().toLowerCase();
  const shown = needle
    ? options.filter((option) => option.value.toLowerCase().includes(needle))
    : options;

  return (
    <Box wid={wid} ref={boxRef} onKeyDown={(e) => e.key === 'Escape' && close()}>
      <Text marg="0 0 4px 4px" b2 med>
        {label}
      </Text>

      <Trigger
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <Label>{selected.value}</Label>
        <Icon type="downThin" dimensions={16} />
      </Trigger>

      {open && (
        <Menu>
          {searchable && (
            <Search
              ref={searchRef}
              placeholder="Search..."
              value={query}
              onChange={({ target }) => setQuery(target.value)}
            />
          )}

          <List role="listbox" aria-label={label}>
            {shown.map((option) => (
              <Item
                key={option.value}
                role="option"
                aria-selected={option.value === selected.value}
                selected={option.value === selected.value}
                onClick={() => choose(option)}
              >
                {option.value}
              </Item>
            ))}
            {!shown.length && <Empty>No match</Empty>}
          </List>
        </Menu>
      )}
    </Box>
  );
};

export default FilterSelect;
